// "Send invoice" modal. We don't run our own email server yet, so we hand
// the user three one-click ways to deliver the invoice:
//   1. Copy the public link to the clipboard
//   2. Open WhatsApp with the customer's phone + a templated message
//   3. Open the user's mail client with a templated email + the link
// All three open with a sensible default message that includes the invoice
// number, amount, due date, and a one-line org sign-off. Better than
// QuickBooks: the WhatsApp path is one click and the message is formatted.

import { useState } from 'react';
import { X, Copy, MessageCircle, Mail, Check, ExternalLink } from 'lucide-react';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney } from '../lib/format';
import type { Contact, Invoice } from '../lib/types';

interface Props {
  invoice: Invoice;
  contact: Contact;
  onClose: () => void;
}

export default function SendInvoiceModal({ invoice, contact, onClose }: Props) {
  const { currentOrg } = useOrg();
  const publicUrl = window.location.origin + '/app/p/invoice/' + invoice.public_token;
  const outstanding = Number(invoice.total) - Number(invoice.amount_paid);

  const defaultMessage =
`Hi ${contact.name},

Please find ${invoice.kind === 'credit_note' ? 'credit note' : 'invoice'} ${invoice.number} for ${formatMoney(invoice.total, invoice.currency)} attached.

Due: ${formatDate(invoice.due_date)}
${outstanding > 0 ? 'Outstanding: ' + formatMoney(outstanding, invoice.currency) : 'This is paid in full.'}
View online: ${publicUrl}

Thanks,
${currentOrg?.name ?? 'DodoBooks'}`;

  const [message, setMessage] = useState(defaultMessage);
  const [copied, setCopied] = useState(false);

  async function copy(text: string) {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { /* clipboard blocked — user can copy manually */ }
  }

  const whatsappPhone = (contact.phone ?? '').replace(/[^\d]/g, '');
  const whatsappHref = whatsappPhone
    ? `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;

  const mailtoHref = contact.email
    ? `mailto:${contact.email}?subject=${encodeURIComponent(`Invoice ${invoice.number} from ${currentOrg?.name ?? 'us'}`)}&body=${encodeURIComponent(message)}`
    : `mailto:?subject=${encodeURIComponent(`Invoice ${invoice.number}`)}&body=${encodeURIComponent(message)}`;

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center px-4">
      <div className="bg-surface border border-line-strong rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-line sticky top-0 bg-surface z-10">
          <div>
            <h2 className="h-section">Send invoice {invoice.number}</h2>
            <p className="text-xs text-text-mute font-mono mt-0.5">To {contact.name}</p>
          </div>
          <button onClick={onClose} className="text-text-mute hover:text-text"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Public link card */}
          <div className="bg-ink-2 border border-line rounded-lg p-4">
            <div className="text-xs font-mono uppercase tracking-widest text-text-mute mb-2">Public link</div>
            <div className="flex items-center gap-2">
              <input readOnly value={publicUrl} className="flex-1 font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
              <button onClick={() => copy(publicUrl)} className="btn btn-secondary btn-sm whitespace-nowrap">
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
            <p className="text-[11px] text-text-mute mt-2">Anyone with this link can view the invoice (no login). Safe to share via WhatsApp, email, SMS or printed on the invoice itself.</p>
          </div>

          {/* Message preview */}
          <div>
            <label className="mb-1 block">Message (edit before sending)</label>
            <textarea rows={9} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full font-mono text-xs" />
          </div>

          {/* Send actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener"
              className="flex items-center gap-3 p-4 rounded-lg border border-line bg-ink-2 hover:border-emerald/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-[#25d366]/15 text-[#25d366] grid place-items-center shrink-0">
                <MessageCircle size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">WhatsApp</div>
                <div className="text-[11px] text-text-mute truncate font-mono">
                  {contact.phone || 'Pick a contact in WhatsApp'}
                </div>
              </div>
              <ExternalLink size={14} className="text-text-mute" />
            </a>

            <a
              href={mailtoHref}
              className="flex items-center gap-3 p-4 rounded-lg border border-line bg-ink-2 hover:border-emerald/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-gold/15 text-gold grid place-items-center shrink-0">
                <Mail size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Email</div>
                <div className="text-[11px] text-text-mute truncate font-mono">
                  {contact.email || 'Will prompt for address'}
                </div>
              </div>
              <ExternalLink size={14} className="text-text-mute" />
            </a>

            <button
              onClick={() => copy(message)}
              className="flex items-center gap-3 p-4 rounded-lg border border-line bg-ink-2 hover:border-emerald/40 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald/15 text-emerald grid place-items-center shrink-0">
                {copied ? <Check size={20} /> : <Copy size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{copied ? 'Copied!' : 'Copy message'}</div>
                <div className="text-[11px] text-text-mute font-mono">Paste anywhere</div>
              </div>
            </button>

            <a
              href={publicUrl}
              target="_blank"
              rel="noopener"
              className="flex items-center gap-3 p-4 rounded-lg border border-line bg-ink-2 hover:border-emerald/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-paper/15 text-paper grid place-items-center shrink-0">
                <ExternalLink size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Preview public view</div>
                <div className="text-[11px] text-text-mute font-mono">See what the customer sees</div>
              </div>
            </a>
          </div>

          <div className="flex justify-end pt-2 border-t border-line">
            <button onClick={onClose} className="btn btn-ghost">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}
